'use client'

import { Drawer, Stack } from '@mantine/core'

import type { PredictionNavNode } from '@/services/prediction/types'

import { NavTreeAccordionProvider, NavTreeItem } from '../NavTree/items'
import itemStyles from '../NavTree/items.module.css'
import { activeChildId, isAllNode, sortByOrdering } from '../NavTree/utils'
import styles from './styles.module.css'

interface SubcategorySheetProps {
  opened: boolean
  onClose: () => void
  /** 激活 L1 root 节点。sheet 渲染其非 `scope` 子节点(第 2 层 folder/destination)
   *  为可导航列表 —— 单个 L1 的子树。 */
  activeRoot: PredictionNavNode
  /** 当前选择路径(同 SecondLevelNavTree 的 selectedPath),驱动行高亮。 */
  selectedPath: string
  /** 选中行节点(含 All/子分类/折叠文件夹)——走上层 handleTreeSelect。 */
  onSelect: (node: PredictionNavNode) => void
}

/**
 * 移动端底部抽屉,作用域为一个 L1 root 的子分类(port 自 cdc-web
 * SubcategorySheet;og-web 用原生-app 风格的 MotionBottomSheet,framer,
 * cdc 无此基础设施)。
 *
 * 用 Mantine `Drawer`(`position="bottom"`, `size="80dvh"`)承载与桌面左树
 * 同一份 data/list 逻辑:激活 L1 的非 scope children、
 * `NavTreeAccordionProvider` + `NavTreeItem surface="sheet"`、选中即关闭
 * (cdc-web 是路由变化自动关闭——本地 state 版改为每次选择后关闭,
 * SecondLevelNavTree 的 onClick 统一处理)。
 *
 * `hiddenFrom="midScale"`(SecondLevelNavTree)保证该抽屉只在移动端可用。
 *
 * theme 说明(与 cdc-web 的差异):cdc-web 用 `usePortalTheme` 把
 * data-theme 属性挂到 Drawer.Content 上,因为它的 token 定义在
 * `[data-theme-type][data-theme-variant]` 容器内。cdc-market 的 token 由
 * Mantine CSSVariablesResolver 注入 `html[data-mantine-color-scheme="dark"]`
 * (portal 到 body 后天然继承),且 theme.ts 已定制
 * `Drawer .content { background: var(--bg-app) }` —— 不挂任何 class,
 * 抽屉自动与页面同一套 token/底色。**不要**学 cdc-web 挂 scope class:
 * 会把 global.css 里 `.theme-og` 那套残 token(橙 active/灰底)带进抽屉,
 * 造成抽屉内外双轨色。
 */
export function SubcategorySheet({
  opened,
  onClose,
  activeRoot,
  selectedPath,
  onSelect,
}: Readonly<SubcategorySheetProps>) {
  const children = sortByOrdering((activeRoot.children ?? []).filter(c => c.node_type !== 'scope'))

  // 做选择即关 drawer(cdc-web 是路由变化自动关——本地 state 版由选择
  // 动作承担同一语义)。
  const handleSelect = (node: PredictionNavNode) => {
    onSelect(node)
    onClose()
  }

  return (
    <Drawer.Root opened={opened} onClose={onClose} position="bottom" size="80dvh">
      <Drawer.Overlay />
      <Drawer.Content classNames={{ content: styles.drawerContent }}>
        <Drawer.Header className={styles.drawerHeader}>
          <Drawer.Title fz={16} fw={600}>
            {activeRoot.display_name}
          </Drawer.Title>
          <Drawer.CloseButton />
        </Drawer.Header>
        <Drawer.Body className={styles.drawerBody}>
          <NavTreeAccordionProvider seedId={activeChildId(children, selectedPath)}>
            <Stack component="ul" gap="3xs" className={itemStyles.list}>
              {children.map(node => (
                <NavTreeItem
                  key={node.id}
                  node={node}
                  surface="sheet"
                  selectedPath={selectedPath}
                  onSelect={handleSelect}
                  forceActive={isAllNode(node) && selectedPath === activeRoot.path}
                />
              ))}
            </Stack>
          </NavTreeAccordionProvider>
        </Drawer.Body>
      </Drawer.Content>
    </Drawer.Root>
  )
}
